import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

function getNotificationPermission() {
  try {
    if (typeof window !== "undefined" && "Notification" in window) {
      return Notification.permission;
    }
  } catch (e) {
    // Safari/iOS may throw when accessing Notification
  }
  return "default";
}

function isNotificationSupported() {
  try {
    return typeof window !== "undefined" && "Notification" in window;
  } catch (e) {
    return false;
  }
}

export function useNotifications() {
  const [permission, setPermission] = useState(getNotificationPermission);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported(isNotificationSupported());
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported) return false;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === "granted";
    } catch {
      return false;
    }
  }, [supported]);

  const sendLocalNotification = useCallback((title, body, url, tag) => {
    if (permission !== "granted") return;
    try {
      if (typeof navigator !== "undefined" && navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "SHOW_NOTIFICATION",
          payload: { title, body, url, tag },
        });
      } else {
        new Notification(title, { body, icon: "/logo192.png", tag });
      }
    } catch (e) {
      // Notification not available
    }
  }, [permission]);

  return {
    permission,
    supported,
    requestPermission,
    sendLocalNotification,
    isEnabled: permission === "granted",
  };
}

export function useNotificationPoller() {
  const { sendLocalNotification, isEnabled } = useNotifications();

  useEffect(() => {
    if (!isEnabled) return;

    let lastCheck = new Date().toISOString();

    const poll = async () => {
      try {
        const token = typeof localStorage !== "undefined" ? localStorage.getItem("chillrefer_token") : null;
        if (!token) return;

        const res = await fetch(`${API}/api/notifications/new?since=${lastCheck}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          lastCheck = new Date().toISOString();
          for (const notif of (data.notifications || [])) {
            sendLocalNotification(notif.title, notif.body, notif.url, notif.tag);
          }
        }
      } catch {
        // Silent fail
      }
    };

    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
  }, [isEnabled, sendLocalNotification]);
}
